/**
 * InvestmentStrategyOnboarding — mandatory 10-question wizard.
 *
 * Blocks trading until submitted. Reads the question bank from
 * `GET /api/investment-strategy/questions`, POSTs answers +
 * signature to `/api/investment-strategy/submit`, then bounces
 * the user back to /app with their new risk profile pinned to
 * the auth context.
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChevronLeft, ChevronRight, ShieldCheck, FileText, CheckCircle2 } from "lucide-react";
import ReadingLevelToggle from "@/components/ReadingLevelToggle";
import QuestionnaireLanguageToggle from "@/components/QuestionnaireLanguageToggle";

const LETTERS = ["A", "B", "C", "D", "E"];

export default function InvestmentStrategyOnboarding() {
    const navigate = useNavigate();
    const { user, refresh } = useAuth();
    const [params]              = useSearchParams();
    const [level, setLevel]         = useState(user?.reading_level || "basic");
    const [language, setLanguage]   = useState((user?.preferred_language || "en").toLowerCase());
    const [questions, setQuestions] = useState(null);
    const [answers, setAnswers]     = useState({});
    const [step, setStep]           = useState(0);       // 0..9 questions, 10 = signature
    const [signature, setSignature] = useState(user?.full_name || "");
    const [busy, setBusy]           = useState(false);
    const [done, setDone]           = useState(null);    // { score, category_label, ... }

    useEffect(() => {
        api.get(`/investment-strategy/questions?level=${level}&language=${language}`)
            .then(({ data }) => setQuestions(data.questions))
            .catch(() => toast.error("Could not load questionnaire — please refresh."));
    }, [level, language]);

    if (!questions) {
        return <div className="p-8 text-sm text-muted-foreground">Loading questionnaire…</div>;
    }

    const total  = questions.length;
    const isSign = step === total;
    const q      = isSign ? null : questions[step];
    const answered = q ? Boolean(answers[q.id]) : false;

    const canGoNext = () => (isSign ? signature.trim().length >= 2 : answered);
    const answeredCount = Object.keys(answers).length;

    async function submit() {
        setBusy(true);
        try {
            const ordered = questions.map((qq) => answers[qq.id]);
            const { data } = await api.post("/investment-strategy/submit", {
                answers: ordered,
                signature: signature.trim(),
                level,
            });
            setDone(data);
            await refresh?.();
            toast.success(`Strategy saved — ${data.category_label}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Submission failed. Please try again.");
        } finally {
            setBusy(false);
        }
    }

    // ── Success state ─────────────────────────────────────────
    if (done) {
        return (
            <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-4 py-10">
                <div className="max-w-2xl w-full space-y-6 text-center" data-testid="strategy-success">
                    <div className="mx-auto w-11 h-11 rounded-xl bg-primary/10 text-primary border border-primary/30 flex items-center justify-center" aria-hidden="true">
                        <CheckCircle2 className="w-5 h-5" strokeWidth={2.25} />
                    </div>
                    <div>
                        <div className="overline text-primary mb-2">Strategy saved</div>
                        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-[1.1]">
                            Your <span className="text-primary">profile</span> is locked in.
                        </h1>
                        <p className="text-base md:text-lg leading-relaxed text-muted-foreground mt-2">
                            Score <b className="text-primary mono">{done.score}</b> / 50 &nbsp;&middot;&nbsp;
                            Profile <b>{done.category_label}</b>
                            &nbsp;<span className="text-xs">({done.risk_level} risk)</span>
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <Button
                            variant="outline"
                            data-testid="strategy-download-pdf"
                            onClick={async () => {
                                const r = await api.get(`/investment-strategy/pdf/${done.strategy_id}`, { responseType: "blob" });
                                const url = URL.createObjectURL(r.data);
                                const a = document.createElement("a");
                                a.href = url; a.download = done.pdf_filename; a.click();
                                URL.revokeObjectURL(url);
                            }}
                        >
                            <FileText className="w-4 h-4 mr-2" /> Download PDF
                        </Button>
                        <Button
                            data-testid="strategy-goto-trading"
                            onClick={() => navigate(params.get("next") || "/app/terminal")}
                        >
                            Continue to trading <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // ── Wizard ────────────────────────────────────────────────
    const progressPct = Math.round(((step) / (total + 1)) * 100);

    return (
        <div className="min-h-screen bg-background text-foreground px-4 py-10" data-testid="strategy-wizard" dir={language === "ar" ? "rtl" : undefined}>
            <div className="max-w-2xl mx-auto space-y-6">
                <div className="flex items-center gap-3">
                    <span className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0" aria-hidden="true">
                        <ShieldCheck className="w-5 h-5" strokeWidth={2.25} />
                    </span>
                    <span className="overline text-primary tracking-widest">Compliance &middot; Required to Trade</span>
                </div>
                <div>
                    <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-[1.1]">
                        Investment <span className="text-primary">Objective</span> &amp; Risk Tolerance
                    </h1>
                    <p className="text-base md:text-lg leading-relaxed text-muted-foreground mt-2">
                        Ten questions. Answers configure your trading permissions and how
                        Obondo AI personalises its guidance. This is a one-time step — you
                        can retake it any time from Settings.
                    </p>
                    <div className="mt-4 flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-muted-foreground">Wording level:</span>
                        <ReadingLevelToggle value={level} onChange={setLevel} />
                    </div>
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-muted-foreground">Language:</span>
                        <QuestionnaireLanguageToggle value={language} onChange={setLanguage} />
                    </div>
                </div>

                {/* Progress bar */}
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <div className="flex-1 h-1.5 bg-border rounded-full overflow-hidden">
                        <div
                            className="h-full bg-primary transition-all"
                            style={{ width: `${progressPct}%` }}
                            data-testid="strategy-progress-bar"
                        />
                    </div>
                    <span className="mono">
                        {isSign ? "Sign" : `Q${step + 1}`} / {total + 1}
                    </span>
                </div>

                {/* Body */}
                <div className="rounded-xl border-2 border-border bg-card p-6 space-y-5" data-testid="strategy-body">
                    {isSign ? (
                        <div className="space-y-4">
                            <div className="text-sm">
                                I acknowledge that the answers I provided reflect my current
                                investment objectives and risk tolerance. I authorise Melian
                                Dialogue to use this statement to configure my trading
                                permissions and to personalise Obondo AI guidance.
                            </div>
                            <Input
                                placeholder="Type your full legal name to sign"
                                value={signature}
                                onChange={(e) => setSignature(e.target.value)}
                                data-testid="strategy-signature-input"
                            />
                            <div className="text-[10px] text-muted-foreground">
                                Answered {answeredCount} / {total} questions.
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                Question {step + 1} of {total}
                            </div>
                            <div className="text-lg font-bold leading-snug">
                                {q.prompt}
                            </div>
                            <div className="space-y-2">
                                {q.options.map((opt, i) => {
                                    const letter = LETTERS[i];
                                    const selected = answers[q.id] === letter;
                                    return (
                                        <button
                                            key={letter}
                                            data-testid={`strategy-opt-${q.id}-${letter}`}
                                            data-selected={selected}
                                            onClick={() => setAnswers((a) => ({ ...a, [q.id]: letter }))}
                                            className={
                                                "w-full text-left rounded-lg border-2 p-3 text-sm transition-colors " +
                                                (selected
                                                    ? "border-primary bg-primary/10"
                                                    : "border-border hover:border-primary/50")
                                            }
                                        >
                                            <div className="flex items-start gap-3">
                                                <span
                                                    className={
                                                        "mt-0.5 shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center text-[11px] font-bold " +
                                                        (selected
                                                            ? "border-primary bg-primary text-primary-foreground"
                                                            : "border-border")
                                                    }
                                                >
                                                    {letter}
                                                </span>
                                                <span className="pt-0.5">{opt.replace(/^[A-E]\)\s*/, "")}</span>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>

                {/* Nav */}
                <div className="flex items-center justify-between gap-3">
                    <Button
                        variant="ghost"
                        disabled={step === 0 || busy}
                        onClick={() => setStep((s) => Math.max(0, s - 1))}
                        data-testid="strategy-prev-btn"
                    >
                        <ChevronLeft className="w-4 h-4 mr-1" /> Back
                    </Button>
                    {isSign ? (
                        <Button
                            disabled={!canGoNext() || busy || answeredCount < total}
                            onClick={submit}
                            data-testid="strategy-submit-btn"
                        >
                            {busy ? "Submitting…" : "Submit & Unlock Trading"}
                        </Button>
                    ) : (
                        <Button
                            disabled={!canGoNext() || busy}
                            onClick={() => setStep((s) => s + 1)}
                            data-testid="strategy-next-btn"
                        >
                            Next <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
